/**
 * Form fields for the settings of a configuration document.
 *
 * A card describes its settings declaratively (`fields`: label, path into the
 * document, kind, limits, unit, help) and this component renders them as a
 * grid of inputs bound to the document. A valid entry lands in the document
 * at once through `onChange(nextConfig)`; an invalid one (empty, not a
 * number, outside the limits, not an integer) stays in the field with its
 * error, leaves the document unchanged and is reported through
 * `onInvalidChange(count)` so the page can block Start. Nothing is ever
 * clamped or rounded on the user's behalf.
 *
 * Field kinds: "number" (default), "integer", "boolean" (switch), "choice"
 * (select over `options: [{ value, label }]`). A `clearable` number field
 * may be left empty: the key is then removed from the document. A choice
 * field's `default` is shown while the document lacks the key (the value
 * the code applies then); picking an option writes it. A field may carry
 * `validate(value)`, returning an error text or null, for a rule that
 * depends on other settings (checked after the numeric limits).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

export function getAt(object, path) {
  let node = object;
  for (const key of path) {
    if (node == null || typeof node !== "object") return undefined;
    node = node[key];
  }
  return node;
}

/** A copy of `object` with `value` at `path`; arrays stay arrays. */
export function setAt(object, path, value) {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const base = Array.isArray(object) ? [...object] : { ...(object || {}) };
  base[head] = setAt(object != null && typeof object === "object" ? object[head] : undefined, rest, value);
  return base;
}

/** A copy of `object` without the key at `path` (unchanged when absent). */
export function deleteAt(object, path) {
  if (object == null || typeof object !== "object") return object;
  const [head, ...rest] = path;
  if (rest.length === 0) {
    if (Array.isArray(object)) return object;
    const { [head]: _removed, ...kept } = object;
    return kept;
  }
  if (!(head in object)) return object;
  const base = Array.isArray(object) ? [...object] : { ...object };
  base[head] = deleteAt(object[head], rest);
  return base;
}

const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

/**
 * Sums the invalid counts of several field groups (keyed) and reports the
 * total through `onInvalidChange`. Returns `setterFor(key)`, a stable
 * callback per key to hand to each group's `onInvalidChange`.
 */
export function useInvalidTotal(onInvalidChange) {
  const counts = useRef({});
  const setters = useRef({});
  const [total, setTotal] = useState(0);
  const setterFor = useCallback((key) => {
    if (!setters.current[key]) {
      setters.current[key] = (count) => {
        if (counts.current[key] === count) return;
        counts.current[key] = count;
        setTotal(Object.values(counts.current).reduce((sum, n) => sum + n, 0));
      };
    }
    return setters.current[key];
  }, []);
  useEffect(() => {
    if (onInvalidChange) onInvalidChange(total);
  }, [total, onInvalidChange]);
  useEffect(
    () => () => {
      if (onInvalidChange) onInvalidChange(0);
    },
    [onInvalidChange]
  );
  return setterFor;
}

/** Why `text` is not a valid value for `field`, or null when it is. */
export function settingError(text, field) {
  const trimmed = String(text ?? "").trim();
  if (trimmed === "") return field.clearable ? null : "Required";
  const value = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(value)) return "Not a number";
  if (field.kind === "integer" && !Number.isInteger(value)) return "Whole number";
  if (isNumber(field.min) && value < field.min) return `At least ${field.min}`;
  if (isNumber(field.exclusiveMin) && value <= field.exclusiveMin) return `Above ${field.exclusiveMin}`;
  if (isNumber(field.max) && value > field.max) return `At most ${field.max}`;
  if (typeof field.validate === "function") return field.validate(value) || null;
  return null;
}

/** The errors of every numeric field against the document (for a page-level check). */
export function settingErrors(config, fields) {
  const errors = {};
  fields.forEach((field) => {
    if (field.kind === "boolean" || field.kind === "choice") return;
    const value = getAt(config, field.path);
    const error = settingError(value == null ? "" : String(value), field);
    if (error) errors[field.path.join(".")] = error;
  });
  return errors;
}

const fieldSx = { width: "100%" };

function NumberField({ field, config, onCommit, draft, setDraft, testId }) {
  const key = field.path.join(".");
  const value = getAt(config, field.path);
  const shown = draft && draft.forValue === value ? draft.text : value == null ? "" : String(value);
  const error = settingError(shown, field);

  const handleChange = (event) => {
    const text = event.target.value;
    const nextError = settingError(text, field);
    if (nextError) {
      setDraft(key, { text, forValue: value });
      return;
    }
    if (field.clearable && text.trim() === "") {
      setDraft(key, { text, forValue: undefined });
      if (value !== undefined) onCommit(deleteAt(config, field.path));
      return;
    }
    const number = Number(text.trim().replace(",", "."));
    setDraft(key, { text, forValue: number });
    if (number !== value) onCommit(setAt(config, field.path, number));
  };

  return (
    <TextField
      label={field.label}
      value={shown}
      onChange={handleChange}
      size="small"
      error={Boolean(error)}
      helperText={error || field.help || " "}
      inputProps={{ inputMode: "decimal", "data-testid": testId }}
      InputProps={
        field.unit ? { endAdornment: <InputAdornment position="end">{field.unit}</InputAdornment> } : undefined
      }
      sx={fieldSx}
    />
  );
}

function BooleanField({ field, config, onCommit, testId }) {
  const value = Boolean(getAt(config, field.path));
  return (
    <Box sx={{ pt: 0.5 }}>
      <FormControlLabel
        control={
          <Switch
            checked={value}
            onChange={(event) => onCommit(setAt(config, field.path, event.target.checked))}
            size="small"
            inputProps={{ "data-testid": testId }}
          />
        }
        label={<Typography variant="body2">{field.label}</Typography>}
      />
      {field.help ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 0.5 }}>
          {field.help}
        </Typography>
      ) : null}
    </Box>
  );
}

function ChoiceField({ field, config, onCommit, testId }) {
  const stored = getAt(config, field.path);
  const value = stored === undefined ? field.default : stored;
  const known = field.options.some((option) => option.value === value);
  const labelId = `${testId}-label`;
  return (
    <FormControl size="small" sx={fieldSx}>
      <InputLabel id={labelId}>{field.label}</InputLabel>
      <Select
        labelId={labelId}
        label={field.label}
        value={known ? value : ""}
        onChange={(event) => onCommit(setAt(config, field.path, event.target.value))}
        inputProps={{ "data-testid": testId }}
      >
        {field.options.map((option) => (
          <MenuItem key={String(option.value)} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {field.help ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
          {field.help}
        </Typography>
      ) : null}
    </FormControl>
  );
}

/**
 * `fields` is every field (for the validity count); `groups` optionally
 * arranges them under titles ([{ title, fields }], empty groups are skipped).
 */
export default function SettingFields({
  config,
  fields,
  groups = null,
  onChange,
  onInvalidChange,
  testPrefix = "setting",
  columns = 3,
}) {
  // What is typed in a field whose text is not (yet) a valid value, keyed by
  // path; a draft is shown only while the document still holds the value it
  // was typed against (a reloaded document shows its own values).
  const [drafts, setDrafts] = useState({});
  const setDraft = (key, draft) => setDrafts((current) => ({ ...current, [key]: draft }));

  const invalidCount = useMemo(() => {
    let count = 0;
    fields.forEach((field) => {
      if (field.kind === "boolean" || field.kind === "choice") return;
      const key = field.path.join(".");
      const value = getAt(config, field.path);
      const draft = drafts[key];
      const shown = draft && draft.forValue === value ? draft.text : value == null ? "" : String(value);
      if (settingError(shown, field)) count += 1;
    });
    return count;
  }, [config, drafts, fields]);

  useEffect(() => {
    if (onInvalidChange) onInvalidChange(invalidCount);
  }, [invalidCount, onInvalidChange]);
  // Fields that disappear (a card hides them) no longer block anything.
  useEffect(
    () => () => {
      if (onInvalidChange) onInvalidChange(0);
    },
    [onInvalidChange]
  );

  const renderField = (field) => {
    const key = field.path.join(".");
    const testId = `${testPrefix}-${key}`;
    if (field.kind === "boolean") {
      return <BooleanField key={key} field={field} config={config} onCommit={onChange} testId={testId} />;
    }
    if (field.kind === "choice") {
      return <ChoiceField key={key} field={field} config={config} onCommit={onChange} testId={testId} />;
    }
    return (
      <NumberField
        key={key}
        field={field}
        config={config}
        onCommit={onChange}
        draft={drafts[key]}
        setDraft={setDraft}
        testId={testId}
      />
    );
  };
  const grid = (list) => (
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: {
          xs: "1fr",
          sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
          md: `repeat(${columns}, minmax(0, 1fr))`,
        },
        width: "100%",
      }}
    >
      {list.map(renderField)}
    </Box>
  );

  if (!groups) return grid(fields);
  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {groups
        .filter((group) => group.fields.length > 0)
        .map((group) => (
          <Stack key={group.title} spacing={1} sx={{ width: "100%" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {group.title}
            </Typography>
            {grid(group.fields)}
          </Stack>
        ))}
    </Stack>
  );
}
